import React from 'react'
import { styled, SxProps } from '@mui/material/styles'
import { primary, defaultText, secondaryText } from '@/constant'
import { ArrowDown } from '@/icon'

export interface MenuItemProps {
  title: string | React.ReactElement;
  subTitle?: string;
  selected?: string | boolean;
  expandable?: boolean;
  expand?: string | boolean;
  sx?: SxProps;
  icon?: React.ReactNode;
  onClick?: () => void;
  onHover?: () => void;
  onLeave?: () => void;
}

const MenuItemComponent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'expand',
})<Pick<MenuItemProps, 'selected' | 'expand'>>(({ selected, expand }) => ({
  fontSize: 16,
  color: expand === 'true' ? primary : defaultText,
  lineHeight: '22px',
  transition: 'all 0.2s linear',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '4px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  gap: '12px',
  minHeight: '48px',
  padding: '6px 24px',
  margin: '0 16px',
  '& > div': {
    width: 'auto',
  },
  ...(selected === 'true' && {
    background: primary,
    color: '#fff',
    boxShadow: '0px 10px 24px 0px rgba(82,196,26,0.5)',
    transition: 'all 0.1s linear',
    '& > div': {
      color: '#fff',
    },
  }),
  '& .menu-icon': {
    fontSize: '14px'
  },
  '& .menu-arrow': {
    display: 'inline-block',
    marginLeft: 'auto',
    transformOrigin: 'center',
    transition: 'transform 0.1s linear',
  },
  ...(expand === 'true' && {
    fontWeight: 'bold',
    '& .menu-title': {
      color: primary,
    },
    '& .menu-arrow': {
      marginLeft: 'auto',
      transform: 'rotate(0.5turn)',
      transition: 'transform 0.1s linear',
      '& > path': {
        fill: primary,
      },
    },
  }),
  ...(expand !== 'true' &&
    selected !== 'true' && {
      '&:hover': {
        background: '#F2F2F2',
        transition: 'all 0.1s linear',
      },
    }),
}))

const MenuItemContent = styled('div')(() => ({
  fontSize: 'inherit',
  color: secondaryText,
}))

const MenuItemWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}))

const MenuItem: React.FC<MenuItemProps> = (props) => {
  const {
    title,
    subTitle,
    selected,
    expand,
    sx,
    onClick,
    onHover,
    onLeave,
    expandable,
    icon,
  } = props

  return (
    <MenuItemWrapper>
      <MenuItemComponent
        key='menu-item-component'
        sx={sx}
        selected={selected?.toString()}
        expand={expand?.toString()}
        // @ts-ignore
        onClick={!selected && !expand ? onClick : null}
        onMouseOver={onHover}
        onMouseLeave={onLeave}
      >
        {icon}
        {title}
        {expandable && (
          <ArrowDown className='menu-arrow' sx={{ fontSize: 14 }} />
        )}
      </MenuItemComponent>
      {subTitle && (
        <MenuItemContent key='menu-item-sub-component'>
          {subTitle}
        </MenuItemContent>
      )}
    </MenuItemWrapper>
  )
}

export default MenuItem
